import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../../../components/ui/dialog';
import { Button } from '../../../../../components/ui/button';
import { Badge } from '../../../../../components/ui/badge';
import { Card, CardContent } from '../../../../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../../components/ui/select';
import { Label } from '../../../../../components/ui/label';
import { cn } from '../../../../../lib/utils';
import { ArrowLeft, Plus } from 'lucide-react';
import type { DashboardProject } from '../../../types/project';
import { fetchSpoolNcrList, registerSpoolNcr } from '../../../../../redux/Spoolsbase/actions';
import { buildNcrRegisterDetailPayload, type NcrRow } from './spoolNcrApi';
import { Notification } from '../../../../../components/ui/custom-notification';

export type { NcrRow } from './spoolNcrApi';

function dispositionBadgeCls(v: NcrRow['dispositionVariant']) {
  if (v === 'pass') return 'border-transparent bg-emerald-50 text-emerald-800';
  if (v === 'warn') return 'border-transparent bg-amber-50 text-amber-900';
  return 'border-transparent bg-slate-100 text-slate-700';
}

function statusBadgeCls(v: NcrRow['statusVariant']) {
  if (v === 'pass') return 'border-transparent bg-emerald-50 text-emerald-800';
  if (v === 'warn') return 'border-transparent bg-amber-50 text-amber-900';
  return 'border-transparent bg-red-50 text-red-800';
}

export interface NCRRegisterModalProps {
  open: boolean;
  onClose: () => void;
  project?: DashboardProject;
  /** Required to load the list: `GET /spool-tracker/ncr?project_id=&pipe_id=` */
  projectId?: string | number | null;
  pipeId?: string | number | null;
  /** When set, adds `&spool_id=` to limit to one spool. */
  spoolId?: number | null;
}

const emptyDetail = {
  rootCause: '',
  correctiveAction: '',
  disposition: 'pending' as const,
};

export const NCRRegisterModal: React.FC<NCRRegisterModalProps> = ({
  open,
  onClose,
  project,
  projectId = null,
  pipeId = null,
  spoolId = null,
}) => {
  const dispatch = useDispatch();
  const { ncrList, ncrListLoading, ncrListError, ncrSaving, ncrSaveErr } = useSelector(
    (s: {
      spoolsbase: {
        ncrList: NcrRow[];
        ncrListLoading: boolean;
        ncrListError: string | null;
        savingStep: string | null;
        saveError: { step: string; message: string } | null;
      };
    }) => ({
      ...s.spoolsbase,
      ncrSaving: s.spoolsbase.savingStep === 'ncr',
      ncrSaveErr:
        s.spoolsbase.saveError?.step === 'ncr' ? s.spoolsbase.saveError?.message ?? null : null,
    })
  );
  const [selectedId, setSelectedId] = useState<string>('');
  const [rootCause, setRootCause] = useState('');
  const [correctiveAction, setCorrectiveAction] = useState('');
  const [disposition, setDisposition] = useState<string>(emptyDetail.disposition);
  const ncrListWasLoadingRef = useRef(false);

  const stats = useMemo(() => {
    const openC = ncrList.filter((r) => r.status === 'Open').length;
    const pending = ncrList.filter((r) => r.status === 'Pending Closure').length;
    const closed = ncrList.filter((r) => r.status === 'Closed').length;
    return { open: openC, pending, closed, total: ncrList.length };
  }, [ncrList]);

  const selected = useMemo(
    () => ncrList.find((r) => r.id === selectedId) ?? null,
    [ncrList, selectedId]
  );

  const syncDetailFromRow = useCallback((row: NcrRow) => {
    setRootCause(row.rootCause);
    setCorrectiveAction(row.correctiveAction);
    setDisposition(row.dispositionSelect);
  }, []);

  useEffect(() => {
    if (!open) return;
    if (projectId == null || projectId === '' || pipeId == null || pipeId === '') {
      dispatch(fetchSpoolNcrList({}));
      return;
    }
    dispatch(
      fetchSpoolNcrList({ projectId, pipeId, spoolId: spoolId != null ? spoolId : undefined })
    );
  }, [open, projectId, pipeId, spoolId, dispatch]);

  useEffect(() => {
    if (!open) {
      ncrListWasLoadingRef.current = false;
      return;
    }
    if (ncrListLoading) {
      ncrListWasLoadingRef.current = true;
      return;
    }
    const loadJustFinished = ncrListWasLoadingRef.current;
    ncrListWasLoadingRef.current = false;
    if (!loadJustFinished) return;
    if (ncrList.length === 0) {
      setSelectedId('');
      setRootCause('');
      setCorrectiveAction('');
      setDisposition('pending');
      return;
    }
    setSelectedId(ncrList[0].id);
    syncDetailFromRow(ncrList[0]);
  }, [open, ncrListLoading, ncrList, syncDetailFromRow]);

  const handleSelectRow = (row: NcrRow) => {
    setSelectedId(row.id);
    syncDetailFromRow(row);
  };

  const submitDetail = (mode: 'save' | 'notify_buyer') => {
    if (projectId == null || projectId === '' || pipeId == null || pipeId === '') {
      Notification.error('NCR', 'Select a pipe in Production, then try again.');
      return;
    }
    if (selected == null) {
      Notification.error('NCR', 'Select an NCR in the table first.');
      return;
    }
    if (selected.spoolId == null || selected.spoolId <= 0) {
      Notification.error('NCR', 'This row is missing spool data. Close and reload the NCR list.');
      return;
    }
    try {
      const payload = buildNcrRegisterDetailPayload(
        selected,
        projectId,
        pipeId,
        rootCause,
        correctiveAction,
        disposition,
        mode === 'notify_buyer' ? 'notify_buyer' : 'save'
      );
      dispatch(registerSpoolNcr(payload as never));
    } catch (e) {
      Notification.error('NCR', (e as Error).message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-screen h-screen max-w-none m-0 p-0 gap-0 border-0 bg-white shadow-none rounded-none [&>button]:hidden">
        <div className="flex h-screen flex-col overflow-hidden">
          <DialogHeader className="flex shrink-0 flex-row items-start justify-between gap-4 border-b bg-white px-4 py-4 sm:px-6">
            <div className="flex min-w-0 flex-1 items-start gap-3">
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="shrink-0"
                onClick={onClose}
                aria-label="Back"
              >
                <ArrowLeft className="h-5 w-5 text-gray-600" />
              </Button>
              <div className="min-w-0">
                <DialogTitle className="text-left text-lg font-bold text-gray-900">
                  Non-Conformance Register
                </DialogTitle>
                {project?.project?.projectName && (
                  <p className="mt-1 text-sm text-gray-500 truncate">{project.project.projectName}</p>
                )}
                {spoolId != null ? (
                  <p className="mt-1 text-xs text-gray-400">Filtered to one spool (id: {String(spoolId)})</p>
                ) : null}
              </div>
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              <Button type="button" size="sm" className="gap-1.5 bg-[#1E6353] hover:bg-[#175544] text-white">
                <Plus className="h-4 w-4" />
                Raise NCR
              </Button>
              {/* <Button type="button" variant="outline" size="sm" onClick={onClose}>
                Close
              </Button> */}
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-4 py-6 sm:px-6">
            <div className="mx-auto max-w-full space-y-6">
              {projectId == null || projectId === '' || pipeId == null || pipeId === '' ? (
                <p className="text-sm text-amber-800 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
                  Select a pipe in Production to load NCRs for this project and line.
                </p>
              ) : null}
              {ncrListError ? (
                <p className="text-sm text-red-700 rounded-md border border-red-200 bg-red-50 px-3 py-2">{ncrListError}</p>
              ) : null}
              {ncrSaveErr ? (
                <p className="text-sm text-red-700 rounded-md border border-red-200 bg-red-50 px-3 py-2">{ncrSaveErr}</p>
              ) : null}
              {ncrListLoading ? (
                <p className="text-sm text-gray-500">Loading NCRs…</p>
              ) : null}
              {/* <div className="rounded-lg border border-amber-200 bg-amber-50/90 px-4 py-3 text-sm text-amber-950">
                <p className="text-xs font-bold uppercase tracking-wide text-amber-800">Product Manager Note</p>
                <p className="mt-2 leading-relaxed">
                  <strong>Replaces</strong>: Paper NCR forms, email chains for BUYER notification, manual tracking
                  spreadsheets.
                  <br />
                  <strong>Key users</strong>: QC Inspector (raises), QC Manager (reviews), BUYER (dispositions).
                  <br />
                  <strong>Workflow</strong>: NCR raised → Root cause analysis → Corrective action → BUYER notified →
                  Disposition → Close.
                </p>
              </div> */}

              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Card className="border-red-200 bg-red-50/60">
                  <CardContent className="p-4">
                    <div className="text-xs font-medium text-red-800">Open</div>
                    <div className="text-2xl font-bold text-red-900">{stats.open}</div>
                  </CardContent>
                </Card>
                <Card className="border-amber-200 bg-amber-50/60">
                  <CardContent className="p-4">
                    <div className="text-xs font-medium text-amber-900">Pending Closure</div>
                    <div className="text-2xl font-bold text-amber-950">{stats.pending}</div>
                  </CardContent>
                </Card>
                <Card className="border-emerald-200 bg-emerald-50/60">
                  <CardContent className="p-4">
                    <div className="text-xs font-medium text-emerald-900">Closed</div>
                    <div className="text-2xl font-bold text-emerald-950">{stats.closed}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="text-xs font-medium text-gray-600">Total</div>
                    <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
                  </CardContent>
                </Card>
              </div>

              <Card className="overflow-hidden border border-gray-200 shadow-sm">
                <CardContent className="p-0">
                  <Table containerClassName="w-full overflow-x-auto">
                    <TableHeader>
                      <TableRow className="bg-gray-50 hover:bg-gray-50">
                        <TableHead className="text-xs font-semibold">NCR #</TableHead>
                        <TableHead className="text-xs font-semibold">Date</TableHead>
                        <TableHead className="text-xs font-semibold">Spool</TableHead>
                        <TableHead className="text-xs font-semibold">Category</TableHead>
                        <TableHead className="text-xs font-semibold">Description</TableHead>
                        <TableHead className="text-xs font-semibold">Disposition</TableHead>
                        <TableHead className="text-xs font-semibold">BUYER Notified</TableHead>
                        <TableHead className="text-xs font-semibold">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {!ncrListLoading && ncrList.length === 0 && !ncrListError && projectId != null && projectId !== '' && pipeId != null && pipeId !== '' ? (
                        <TableRow>
                          <TableCell colSpan={8} className="py-8 text-center text-sm text-gray-500">
                            No NCRs for this project{spoolId != null ? ' and spool' : ' and pipe'}.
                          </TableCell>
                        </TableRow>
                      ) : null}
                      {ncrList.map((row) => (
                        <TableRow
                          key={row.id}
                          data-state={selectedId === row.id ? 'selected' : undefined}
                          className={`cursor-pointer ${selectedId === row.id ? 'bg-blue-50/80' : ''}`}
                          onClick={() => handleSelectRow(row)}
                        >
                          <TableCell className="font-mono text-sm font-semibold">{row.ncrNo}</TableCell>
                          <TableCell className="text-sm text-gray-700">{row.date}</TableCell>
                          <TableCell className="text-sm text-gray-800">{row.spool}</TableCell>
                          <TableCell className="text-sm text-gray-700">{row.category}</TableCell>
                          <TableCell className="max-w-[220px] text-xs text-gray-700">{row.description}</TableCell>
                          <TableCell>
                            <Badge className={`text-[10px] ${dispositionBadgeCls(row.dispositionVariant)}`}>
                              {row.disposition}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-gray-700">{row.buyerNotified}</TableCell>
                          <TableCell>
                            <Badge className={`text-[10px] ${statusBadgeCls(row.statusVariant)}`}>{row.status}</Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              <Card className="overflow-hidden border border-gray-200 shadow-sm">
                <div className="flex flex-col gap-2 border-b bg-red-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-6">
                  <h3 className="text-base font-semibold text-gray-900">
                    {selected ? `${selected.ncrNo} — Detail` : 'NCR detail'}
                  </h3>
                  <Badge variant="outline" className="w-fit border-blue-200 bg-blue-50 text-blue-800">
                    QC Inspector
                  </Badge>
                </div>
                <CardContent className="space-y-4 p-4 sm:p-6">
                  {selected == null ? (
                    <p className="text-sm text-gray-500">Select a row in the table to view and edit details.</p>
                  ) : (
                    <>
                      <div className="grid gap-4 md:grid-cols-2">
                        <div className="space-y-2">
                          <Label className="text-sm font-medium text-gray-800">Root Cause</Label>
                          <textarea
                            className={cn(
                              'flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm',
                              'ring-offset-background placeholder:text-muted-foreground',
                              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                              'disabled:cursor-not-allowed disabled:opacity-50'
                            )}
                            placeholder="Describe root cause of non-conformance..."
                            value={rootCause}
                            onChange={(e) => setRootCause(e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-medium text-gray-800">Corrective Action</Label>
                          <textarea
                            className={cn(
                              'flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm',
                              'ring-offset-background placeholder:text-muted-foreground',
                              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                              'disabled:cursor-not-allowed disabled:opacity-50'
                            )}
                            placeholder="Proposed corrective action..."
                            value={correctiveAction}
                            onChange={(e) => setCorrectiveAction(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-gray-800">Disposition (BUYER to confirm)</Label>
                        <Select value={disposition} onValueChange={setDisposition}>
                          <SelectTrigger className="max-w-md">
                            <SelectValue placeholder="Select disposition" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">— Pending BUYER —</SelectItem>
                            <SelectItem value="accept">Accept As-Is</SelectItem>
                            <SelectItem value="rework">Rework</SelectItem>
                            <SelectItem value="repair">Repair</SelectItem>
                            <SelectItem value="reject">Reject</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex flex-wrap gap-2 pt-2">
                        <Button
                          type="button"
                          className="bg-amber-600 hover:bg-amber-700 text-white"
                          disabled={ncrSaving || ncrListLoading}
                          onClick={() => void submitDetail('notify_buyer')}
                        >
                          {ncrSaving ? '…' : 'Notify BUYER'}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          disabled={ncrSaving || ncrListLoading}
                          onClick={() => void submitDetail('save')}
                        >
                          {ncrSaving ? 'Saving…' : 'Save'}
                        </Button>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
